import {
  AfterViewInit,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  resolveForwardRef,
} from '@angular/core';
import { SelectWidget } from '@app/finxone-web-frontend/app/lib/actions/grid.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { getComponentByLabel } from '@app/finxone-web-frontend/app/lib/utils/get-component.utils';
import { transformCSSWithDynamicSelector } from '@app/finxone-web-frontend/app/lib/utils/themeing.utils';
import { getWidgetClasses } from '@app/finxone-web-frontend/app/widget.utis';
import {
  GLOBAL_TAB_STYLING_CLASS,
  NewUiZoneWidgetConfig,
  ProviderClass,
  StyleState,
  TabsOrientationType,
  cssToNgStyle,
  is2dArray,
  isLayoutWidget,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';

import { createChildWidgetInjector } from '../../zones/utils';
import { ChildComponentList } from '../../zones/widget-layout/main-widget/main-widget.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-tab-widget',
  template: `
    <section [ngStyle]="outerCss" class="no-ripple">
      <hlm-tabs
        [orientation]="tabOrientation"
        [ngClass]="tabOrientation === tabsOrientationType.VERTICAL ? 'd-flex' : ''"
        [class]="tabsScopeClasses"
        [tabId]="this.dataIn.id ?? ''"
        [tab]="String(this.activeIndex)"
        class="w-full"
      >
        <hlm-tabs-list class="z-[1] flex grow overflow-hidden" [orientation]="tabOrientation">
          @for (array of injectorComponentsArrays; track array; let index = $index) {
            <button
              class="relative grow transition-transform tab-item"
              [hlmTabsTrigger]="String(index)"
              [dynamicStyle]="tabStyling()"
              (click)="onTabClick(index)"
            >
              {{ tabHeader(index) }}
            </button>
          }
        </hlm-tabs-list>
        @for (array of injectorComponentsArrays; track array; let index = $index) {
          <div [hlmTabsContent]="String(index)" class="tab-item-content">
            <section [ngStyle]="tabCss(index)">
              <ng-container *ngFor="let component of injectorArray(array)">
                @if (configService.getActiveConfigType().isWorkflow) {
                  <div
                    [ngClass]="component.widget.uniqueId === mouseOverId ? mouseOver : ''"
                    (click)="selectWidget($event, component.widget)"
                    (mouseover)="changeStyle($event, component.widget.uniqueId.toString())"
                    (mouseout)="changeStyle($event)"
                  >
                    <div [class]="getWidgetClasses(component.widget)">
                      <ng-container
                        *ngComponentOutlet="component.component; injector: component.injector"
                      ></ng-container>
                    </div>
                  </div>
                } @else {
                  <div *ngIf="isVisible[index]; else placeholder" class="lazy-content">
                    <div [class]="getWidgetClasses(component.widget)">
                      <ng-container
                        *ngComponentOutlet="component.component; injector: component.injector"
                      ></ng-container>
                    </div>
                  </div>
                  <ng-template #placeholder>
                    <div class="placeholder" lazyLoad (visible)="onVisible(index)"></div>
                  </ng-template>
                }
              </ng-container>
            </section>
          </div>
        }
      </hlm-tabs>
    </section>
  `,
  styles: [],
})
export class TabWidgetComponent implements OnInit, OnDestroy, AfterViewInit {
  protected readonly String = String;
  public activeIndex = 0;
  public tabsOrientationType = TabsOrientationType;
  public injectorComponentsArrays: {
    [key: string]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component: any;
      injector: Injector;
      widget: NewUiZoneWidgetConfig;
    };
  }[] = [];
  public isVisible: { [key: number]: boolean } = {};
  private isWorkflowType: boolean;

  public injectorArray(object: {
    [key: string]: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      component: any;
      injector: Injector;
      widget: NewUiZoneWidgetConfig;
    };
  }) {
    return Object.values(object);
  }

  public mouseOver = '';
  public mouseOverId = '';

  constructor(
    public childComponents: ChildComponentList,
    public dataIn: ProviderClass<object>,
    private injector: Injector,
    public configService: ConfigService,
    private store: Store,
    private readonly renderer: Renderer2,
  ) {
    this.checkForTabRefAndSetActiveTab();
  }

  ngOnInit(): void {
    if (is2dArray(this.childComponents.list)) {
      const tabs = this.childComponents.list as NewUiZoneWidgetConfig[][];
      this.injectorComponentsArrays = Array.from(Array(tabs.length), () => {
        return {};
      });
      tabs.forEach((tabComponents, index) => {
        tabComponents.forEach((widget) => {
          const component = resolveForwardRef(getComponentByLabel(widget.name));
          this.injectorComponentsArrays[index][widget.uniqueId] = {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            component: component,
            injector: createChildWidgetInjector(
              widget,
              this.injector,
              this.dataIn,
              isLayoutWidget(widget.name)
                ? [
                    {
                      provide: ChildComponentList,
                      useValue: new ChildComponentList(widget.childComponents ?? []),
                    },
                  ]
                : [],
            ),
            widget: widget,
          };
        });
      });
      this.isWorkflowType = this.configService.getActiveConfigType()?.isWorkflow;
    } else {
      throw new Error('Two column widget cannot have one child component arrays or more than 2');
    }
  }

  ngAfterViewInit(): void {
    this.setupTabsStyling();
    this.onVisible(this.activeIndex ?? 0);
  }

  getWidgetClasses = getWidgetClasses;

  get outerCss() {
    return cssToNgStyle(
      this.isGlobalStylingEnabled ? '' : (this.dataIn.widgetConfig.attributes.outerCss ?? ''),
    );
  }

  public async setupTabsStyling() {
    const css = this.dataIn.widgetConfig?.attributes.css_config?.elementStyling?.['tabCss']?.toString() ?? '';
    const elementSelector = 'hlm-tabs.tabs-' + this.dataIn.uniqueId;
    const tabEl = document.getElementsByClassName('tabs-' + this.dataIn.uniqueId)[0];
    const styleElementSelector = elementSelector + '-style';
    this.removeExistingTabsStyle(styleElementSelector);
    if (this.isGlobalStylingEnabled) {
      return;
    }
    const styles = transformCSSWithDynamicSelector(css, elementSelector);
    if (tabEl) {
      const styleEl = this.renderer.createElement('style');
      styleEl.id = styleElementSelector;
      styleEl.innerHTML = styles;
      document.head.append(styleEl);
    }
  }

  private removeExistingTabsStyle(styleElementSelector: string) {
    const styleEl = document.getElementById(styleElementSelector);
    if (styleEl) document.head.removeChild(styleEl);
  }

  public tabHeader(index: number) {
    return this.dataIn.widgetConfig.attributes.widgetProperties['tabConfigs'][index]?.title ?? 'Header';
  }

  public tabCss(index: number) {
    return cssToNgStyle(
      this.dataIn.widgetConfig.attributes.widgetProperties['tabConfigs'][index]?.outerCssConfig ?? '',
    );
  }

  public tabStyling(): StyleState {
    return this.dataIn.widgetConfig.attributes.css_config?.['elementStyling']?.['tab'] ?? {};
  }

  ngOnDestroy(): void {
    const elementSelector = 'hlm-tabs.tabs-' + this.dataIn.uniqueId;
    const styleElementSelector = elementSelector + '-style';
    this.removeExistingTabsStyle(styleElementSelector);
  }

  public onTabClick(index: number) {
    this.activeIndex = index;
    this.isVisible[index] = false;
    window.location.hash = this.activeTabRef || '';
  }

  private get activeTabRef(): string | undefined | '' {
    return this.dataIn.widgetConfig.attributes.widgetProperties['tabConfigs']?.[this.activeIndex]?.tabRef;
  }

  public onVisible(tabIndex: number) {
    this.isVisible[tabIndex] = true;
  }

  changeStyle($event: any, mouseOverId?: string) {
    this.mouseOver = $event.type == 'mouseover' ? 'widget-hover' : '';
    if (mouseOverId) this.mouseOverId = mouseOverId;
    $event.stopPropagation();
  }

  selectWidget(event: MouseEvent, widget: NewUiZoneWidgetConfig) {
    if (this.isWorkflowType) {
      if (event.shiftKey) {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), true));
      } else {
        this.store.dispatch(new SelectWidget(widget.uniqueId.toString(), false));
      }
    }
    event.stopPropagation();
  }

  get isGlobalStylingEnabled(): boolean {
    return this.dataIn.widgetConfig.attributes.widgetProperties?.['globalStyling'] ?? false;
  }

  get tabsScopeClasses(): string {
    const scope = `tabs-${this.dataIn?.uniqueId}`;
    return this.isGlobalStylingEnabled ? `${scope} ${GLOBAL_TAB_STYLING_CLASS}` : scope;
  }

  get tabOrientation(): TabsOrientationType {
    return (
      this.dataIn.widgetConfig.attributes.widgetProperties?.['tabOrientationType'] ??
      TabsOrientationType.HORIZONTAL
    );
  }

  private checkForTabRefAndSetActiveTab() {
    const tabConfigs = this.dataIn.widgetConfig.attributes.widgetProperties['tabConfigs'];

    if (!tabConfigs?.length) return;

    // Extract the fragment identifier from the URL hash (e.g., '#tab3?direction=forward' → 'tab3')
    const hash = window.location.hash.slice(1).match(/^\w+/)?.[0] || null;

    // Find the tab index that matches the hash
    const matchingIndex = tabConfigs.findIndex((tabConfig: any) => tabConfig.tabRef === hash);

    // Set the activeIndex to the matching tab index, or default to 0 if not found
    this.activeIndex =
      matchingIndex !== -1
        ? matchingIndex
        : (this.dataIn.widgetConfig.attributes.widgetProperties['tabInitialIndex'] ?? 0);
  }
}
